import { timestamp } from "../../lib/format";
import { linkPurposeLabel, signInLinkUrl } from "../../lib/operator";
import type { IssuedLink } from "../../lib/types";
import { Button } from "../ui/Button";
import { Copyable } from "../ui/Copy";
import { MicroLabel } from "../ui/Text";

/**
 * A sign-in link, shown the one time it exists in readable form.
 *
 * The server stores only a hash of the token, so once this panel is dismissed —
 * or the page reloaded — there is no way to see the link again; the answer is to
 * issue a new one, which revokes this one. The panel says so in plain words,
 * because the natural assumption is that a link can be looked up later.
 *
 * Built from `window.location.origin` rather than a configured URL: the panel and
 * the API share an origin, so the address the operator is looking at is the
 * address the colleague needs.
 */
export function IssuedLinkPanel({
  link,
  recipient,
  onDismiss,
}: {
  readonly link: IssuedLink;
  /** Whose link it is, so a copied link is not pasted to the wrong person. */
  readonly recipient: string;
  readonly onDismiss: () => void;
}) {
  const url = signInLinkUrl(window.location.origin, link.token);

  return (
    <section
      aria-label="New sign-in link"
      className="flex flex-col gap-4 border-l-2 border-signal bg-paper-raised px-4 py-4"
    >
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>
          {linkPurposeLabel(link.purpose)} for {recipient}
        </MicroLabel>
        <Button variant="ghost" onClick={onDismiss}>
          Done
        </Button>
      </div>

      <Copyable
        label="Sign-in link"
        value={url}
        hint={`Works once, until ${timestamp(link.expiresAt, "UTC")} UTC.`}
      />

      <p className="m-0 max-w-prose text-body text-ink-muted">
        Send this to {recipient} privately — anyone holding it can set the password for this
        account. <strong className="font-semibold text-ink">It will not be shown again.</strong> If
        it is lost, issue a new link; that cancels this one.
      </p>
    </section>
  );
}
